'use client';

/**
 * **The add-inventory dialog on the attribute treatment** ([INVDRAW-17] /
 * VALE-98) — reached only from the matter Inventory tab, and since VALE-316 /
 * `[INVDRAW-21]` the **only** thing that tab's Add dialog renders. It shipped
 * behind the `inventory-attribute-drawer` runtime flag alongside the drawer, one
 * flag for one treatment; the cutover retired the flag and the `InventoryFormCard`
 * arm it used to select.
 *
 * Plain text at rest, an editor in the value's own slot on click, rendered from
 * the **same per-type spec builders** `[INVDRAW-5…13]` produced. Nothing here
 * knows what a `bank-account` or a `mortgage` looks like; it hands
 * {@link inventoryBlocks} a context and renders what comes back, exactly as
 * {@link InventoryAttributeDrawer} does.
 *
 * ## The one thing that is different: `onCommit` buffers, and Save is the write
 *
 * The drawer commits one field per blur straight to the server, and that is why
 * it has no footer. **A new item has nothing to commit against** — the
 * Contacts precedent (LF-6306) that kept add mode off the treatment there, and
 * a real mechanical fact rather than a preference. So this surface keeps the
 * footer and inverts only the destination:
 *
 * · every row's `toPatch` still produces the same `UpdateInventoryItemRequest`
 *   shape it produces in the drawer;
 * · {@link applyPatch} merges it into **local state** instead of sending it;
 * · `Save` builds one create payload out of the accumulated buffer and fires a
 *   single `POST`.
 *
 * Committing into state rather than to a server is not a swallowed write:
 * `useAttributeCommit`'s contract is that a resolved `onSave` means *accepted*,
 * and here it genuinely is — the value is in the record the footer will send.
 * The row re-derives its display from that same buffer on the next render, so a
 * committed field reads back exactly as it does in the drawer.
 *
 * ## One row the drawer has and this surface does not
 *
 * Because a create request cannot carry it, matching what the flag-off add dialog
 * already does:
 *
 * · **`Status`.** Drawer *chrome*, rendered through `AttributeChromeValue` and
 *   never part of a block, so this surface simply does not render it — the
 *   treatment's equivalent of the flag-off path's `showStatusDropdown={false}`.
 *   A new item is created `draft` by the backend.
 * (**The value-confirmation rows used to be a third**, hidden under
 * `InventoryBlockContext.bufferedRecord` because `CreateInventoryItemRequest` carried no
 * confirmation member. VALE-355 widened the create path instead — the intent rides
 * in the buffer like any other field, is lifted onto the create payload by
 * `useAddInventoryDialog`, and `createInventoryItem` applies it through the same
 * `reconcileValueConfirmationOnUpdate` the PUT uses. So a source known while
 * typing the balance is now recorded by the one `POST`, and this surface renders
 * those rows. The flag-off dialog still does not — it never passes
 * `showValueConfirmation`, whose own doc comment requires a persisted `item.id`.)
 *
 * ## Where the footer sits, and how leaving is guarded
 *
 * **Pinned to the bottom of the sheet's scroll area**, under a rule spanning the
 * sheet's own gutters: the `min-h-full` flex column plus the `flex-1` spacer
 * below the record push it there, so a short record does not leave Save floating
 * mid-panel over empty space.
 *
 * The `[INVDRAW-15]` dogfood read that as detached modal chrome and first asked
 * for it to be reattached under the last field; shown both, the owner's call was
 * to **keep it pinned** and make leaving safe instead (VALE-164):
 *
 * · **Cancel is shown** beside Save — the sheet has its own `X`, but a buffered
 *   write deserves an explicit way out sitting next to it.
 * · **Every exit is guarded.** Because this surface buffers, a dismiss destroys
 *   real work — so `AddInventoryDialog` routes its `X`, `Escape` and this
 *   footer's Cancel through `useUnsavedChangesGuard` and prompts. The Inventory
 *   tab's *drawer* needs none of this: it writes each field on blur, so nothing
 *   is ever unsaved.
 *
 * Note the earlier wording here — "inline at the end of the record … pinning it
 * would mean restructuring `AddInventoryDialog`'s layout" — described the
 * opposite of the code, which has had the spacer since VALE-98. Corrected rather
 * than acted on, since the layout it misdescribed is the one being kept.
 */

import { useCallback, useRef, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AttributeSections,
  AttributeUnsavedStateProvider,
  type AttributeSurfaceControl
} from '@/components/attributes';
import { FormActions } from '@/components/inventory/inventory-form-card/shared/FormActions';
import {
  inventoryBlocks,
  type InventoryPatch
} from '@/components/inventory/drawers/inventory-attribute-specs';
import {
  isSharePriceType,
  shareScopeFor,
  useSharePriceSync
} from '@/components/inventory/drawers/share-price-sync';
import { useInventoryBlockData } from '@/components/inventory/drawers/use-inventory-block-data';
import { useInventoryService } from '@/lib/services/inventory-items';
import type {
  InventoryItem,
  InventoryItemStatus,
  InventoryItemType as InventoryItemTypeSlug
} from '@/lib/services/inventory-items/types';
import type {
  CustomInstitution,
  Institution
} from '@/components/inventory/inventory-form-card/shared/InstitutionSearchField';
import type { InventoryItemType } from '@/lib/services/inventory-types';

/**
 * Merge one row's patch into the buffered record.
 *
 * **`itemInfo` merges; every other key replaces.** That is the same contract
 * the backend applies to an update (`{ ...existing, ...incoming }` for the
 * blob), which is what lets a spec's `infoPatch` — a single-key `itemInfo`
 * patch — mean the same thing here as it does in the drawer. Replacing the
 * blob wholesale would make every commit erase the one before it.
 *
 * A cleared `info` field arrives as `''` rather than `null`, exactly as
 * {@link infoPatch} sends it, and is passed through untouched — the backend's
 * own `emptyStringToNull` is what turns it into a cleared value, on create the
 * same as on update.
 *
 * **`type` is dropped rather than merged.** No spec builder patches it — the
 * record's type is its identity, chosen in the type-selection view and read
 * from `selectedType.slug` when the create payload is built. `InventoryPatch`
 * types it as a plain `string` (the wire shape), so letting it through would
 * widen the buffer's own slug discriminator to `string` for a write that never
 * happens.
 */
export function applyPatch(
  previous: InventoryItem,
  patch: InventoryPatch
): InventoryItem {
  const { itemInfo, type: _type, ...rest } = patch;
  const next: InventoryItem = { ...previous };

  /*
    **A `null` in a patch means "clear", and a record that does not exist yet
    has nothing to clear — so it lands as absent.** This is also what reconciles
    the two shapes the buffer sits between: `UpdateInventoryItemRequest` types
    every clearable key as `T | null` (the wire's way of saying "unset this"),
    while `InventoryItem` types it as `T | undefined`. `??` rather than
    falsiness, so a legitimate `false` or `0` is kept.
  */
  const target = next as unknown as Record<string, unknown>;
  for (const [key, value] of Object.entries(rest)) {
    target[key] = value ?? undefined;
  }

  if (itemInfo) {
    next.itemInfo = { ...(previous.itemInfo ?? {}), ...itemInfo };
  }
  return next;
}

/**
 * The create payload's own shape, as `useAddInventoryDialog.handleSubmit`
 * expects it: the `itemInfo` blob flattened to the top level, `estateReference`
 * beside it, and the whole record under `_fullItem` for the institution /
 * payee / linked-asset resolution that hook already owns.
 *
 * **Bound to that hook's contract rather than restating it** — this is
 * byte-for-byte what the flag-off `InventoryFormContent.handleSave` builds, so
 * the two paths create an item through one code path and cannot drift on how a
 * custom institution or a non-estate flag reaches the API.
 */
export function toSubmitData(draft: InventoryItem): Record<string, unknown> {
  return {
    ...(draft.itemInfo ?? {}),
    estateReference: draft.estateReference,
    _fullItem: draft
  };
}

export interface InventoryAttributeFormViewProps {
  selectedType: InventoryItemType;
  formData: {
    institution_id: string;
    estateReference: string;
  };
  dynamicFormData: Record<string, unknown>;
  submitting: boolean;
  onBack: () => void;
  onSubmit: (dynamicData: Record<string, unknown>) => void;
  onCancel: () => void;
  dateOfDeath?: string;
  matterId?: number;
  /**
   * Whether the reader has entered anything into the buffer, reported up so the
   * hosting dialog can guard a dismiss that would discard it (VALE-164).
   */
  onDirtyChange?: (isDirty: boolean) => void;
  /**
   * The firm-as-payee default for a `legal-professional` liability (LF-5470),
   * resolved by {@link InventoryFormView} before this mounts. Seeded into the
   * buffer as the record's institution, the same one-shot initialisation the
   * flag-off card does.
   */
  defaultServiceProvider?: CustomInstitution;
}

export function InventoryAttributeFormView({
  selectedType,
  formData,
  dynamicFormData,
  submitting,
  onBack,
  onSubmit,
  onCancel,
  dateOfDeath,
  matterId,
  onDirtyChange,
  defaultServiceProvider
}: InventoryAttributeFormViewProps) {
  const typeSlug = selectedType?.slug || 'other';

  /*
    The buffered record. A one-shot initialiser, like the flag-off view's own
    `useState(() => ...)`: re-seeding it from props would throw away everything
    committed since the dialog opened.

    `id: 0` is a placeholder for a record that does not exist yet. Nothing
    reads it — no spec builder touches `item.id` (the entitlement-share lookup
    that would is passed `itemId: undefined` below), and the create payload is
    built from the buffer's own fields, never from an id.
  */
  const [draft, setDraft] = useState<InventoryItem>(() => ({
    id: 0,
    type: typeSlug as InventoryItemTypeSlug,
    status: 'draft' as InventoryItemStatus,
    createdAt: '',
    updatedAt: '',
    estateReference: formData.estateReference || '',
    institutionId: formData.institution_id
      ? parseInt(formData.institution_id, 10)
      : undefined,
    ...(defaultServiceProvider ? { institution: defaultServiceProvider } : {}),
    itemInfo: dynamicFormData || {}
  }));

  /*
    Whether the reader has actually put something in the record — what gates
    Save, mirroring the flag-off footer's `hasUserEdits` rather than RHF's raw
    `isDirty`. A seeded default (the firm as service provider) is not an edit,
    so this starts `false` even when the buffer starts non-empty.
  */
  const [dirty, setDirty] = useState(false);
  /*
    **An editor is open holding something the reader typed** (VALE-1274) — it
    arms Save alongside {@link dirty}, and {@link handleSave} commits it before
    it submits. The contacts create form carries the identical pair, and both
    are here for the same reason: a popover row commits when it is dismissed, so
    a `Save` that is disabled until a row commits cannot be the click that
    dismisses it. `contact-attribute-create-form.tsx` has the long note.
  */
  const [hasOpenDraft, setHasOpenDraft] = useState(false);
  /** A live mirror of the buffer — `Save` writes to it and reads it back in one
      gesture, and a `setDraft` updater has not run by then. */
  const draftRef = useRef<InventoryItem>(draft);
  /** The surface's handle on whatever editor a grid inside has open. */
  const attributeControlRef = useRef<AttributeSurfaceControl | null>(null);

  /**
   * Mark the buffer dirty — locally, **and** for the hosting dialog's discard
   * guard (VALE-164) — in one call.
   *
   * **Reported from the commit itself rather than from an effect on `dirty`**,
   * which is what closes a real window: an effect defers the parent's update by
   * a render cycle, so a dismiss arriving inside that cycle reads
   * `isDirty === false` and takes the buffer with it, silently. Measured, not
   * theorised — a Playwright `Tab` immediately followed by `Escape` lost a
   * committed BSB every time, while the same two keys a frame apart prompted
   * correctly. A human is unlikely to win that race, but the guard exists so
   * that a buffered write is never lost quietly, so it should not be winnable
   * at all.
   *
   * Safe as a plain call rather than an effect: both call sites are event
   * handlers, so this batches with their own `setDraft` instead of being a
   * parent update during render. And `dirty` only ever goes false → true, so
   * there is no later state an effect would need to synchronise back.
   */
  const markDirty = useCallback(() => {
    setDirty(true);
    onDirtyChange?.(true);
  }, [onDirtyChange]);

  const { items } = useInventoryService(matterId ?? 0);

  const blockData = useInventoryBlockData({
    typeSlug,
    matterId: matterId ?? 0,
    items,
    // No id: an unsaved item has no entitlements pointing at it yet.
    itemId: undefined,
    // A different type re-draws the whole record, so the pending payee choice
    // that belonged to the previous one must not survive a Change Type.
    resetKey: typeSlug
  });

  /**
   * One row's commit. Resolves — which `useAttributeCommit` reads as
   * *accepted* — because the value really has been recorded, in the buffer the
   * footer will send. Nothing can reject here: there is no request to fail.
   */
  /*
    Through the ref first and the state from it, rather than a `setDraft`
    updater: `Save` flushes the open editor and then reads the buffer inside the
    same handler, and an updater has not run by then (VALE-1274).
  */
  const writeDraft = useCallback(
    (next: (previous: InventoryItem) => InventoryItem) => {
      const merged = next(draftRef.current);
      draftRef.current = merged;
      setDraft(merged);
    },
    []
  );

  const handleCommit = useCallback(
    async (patch: InventoryPatch) => {
      writeDraft((previous) => applyPatch(previous, patch));
      markDirty();
    },
    [markDirty, writeDraft]
  );

  /**
   * A kept component's institution choice. Unlike the drawer, this does **not**
   * go through `resolveInstitutionUpdateFields`: that helper resolves a
   * *change* against a persisted record (the custom-institution sentinel, the
   * editable-status gate), and on create there is nothing to resolve against.
   * `useAddInventoryDialog.handleSubmit` reads `_fullItem.institution` and
   * performs the equivalent mapping itself, exactly as it does for the
   * flag-off card — so storing the raw selection is what keeps the two paths
   * identical at the API.
   */
  const handleInstitutionChange = useCallback(
    (institution: Institution | CustomInstitution | string | null) => {
      if (typeof institution === 'string') return;
      writeDraft((previous) => ({
        ...previous,
        institution: institution ?? undefined,
        // A chosen institution supersedes the id the dialog was opened with;
        // leaving both would let `handleSubmit`'s `institutionId` fallback win
        // over the selection.
        institutionId: undefined
      }));
      markDirty();
    },
    [markDirty, writeDraft]
  );

  /*
    **The share types' market figures** (VALE-1008), on the same seam the drawer
    uses. `handleCommit` buffers here rather than sending, so an auto-fetched
    price lands in the draft the footer's single `POST` carries, exactly as the
    kept card's own fetch effect did before these became rows.
  */
  const sharePrice = useSharePriceSync({
    item: draft,
    scope: shareScopeFor(typeSlug),
    dateOfDeath,
    enabled: isSharePriceType(typeSlug),
    onCommit: handleCommit
  });

  const blockContext = {
    ...blockData,
    item: draft,
    typeSlug,
    onInstitutionChange: handleInstitutionChange,
    /*
      A record being created is `draft`, which is inside
      `INSTITUTION_EDITABLE_STATUSES` — so the facility is always changeable
      here, and `Estate reference` (gated on the inverse) is always hidden,
      matching the flag-off form's identical condition.
    */
    isInstitutionEditable: true,
    onCommit: handleCommit,
    bufferedRecord: true,
    dateOfDeath,
    sharePrice
  };

  /*
    `blockContext` carries `onCommit` / `onInstitutionChange`, which close over
    the buffer ref (VALE-1274). `react-hooks/refs` sees a ref reaching a function
    called during render and assumes it may be read there — it is not:
    `inventoryBlocks` only stores those callbacks on the specs it builds, and
    both are invoked from a commit, which is an event. (The contacts create form
    holds the identical pair and does not trip the rule, only because it passes
    them as JSX props rather than as an argument.)
  */
  // eslint-disable-next-line react-hooks/refs
  const blocks = inventoryBlocks(blockContext);

  const handleSave = useCallback(() => {
    /*
      **Commit what is on screen before submitting** (VALE-1274) — a row the
      reader typed into but has not dismissed is theirs. A refusal stops the
      submit: the row is now wearing its reason.
    */
    if (attributeControlRef.current?.flushOpenEditors() === false) return;
    onSubmit(toSubmitData(draftRef.current));
  }, [onSubmit]);

  return (
    /*
      Fills the sheet's scroll area so the footer below sits at the **bottom**
      of the panel rather than wherever the record happens to end. Without it a
      short record (a `bank-account` with nothing entered is eight rows) leaves
      Save floating mid-panel over a third of a screen of white — the flag-off
      path never shows that because `InventoryFormCard` draws a `Card` border
      its inline actions read as attached to, and this treatment has no card.
    */
    <div className="flex min-h-full flex-col">
      <div className="mb-4 flex items-center gap-2">
        <Button
          gap="loose"
          variant="ghost"
          size="sm"
          onClick={onBack}
          className="flex"
        >
          <ArrowLeft className="h-4 w-4" />
          Change Type
        </Button>
      </div>

      {/*
        The provider is here for the footer's sake, not for a leave guard — the
        hosting dialog owns that (VALE-164) and no `onUnsavedChange` is passed.
        It is what gives `Save` a handle on the open editor (VALE-1274).
      */}
      <AttributeUnsavedStateProvider
        onUncommittedDraftChange={setHasOpenDraft}
        controlRef={attributeControlRef}
      >
        <AttributeSections
          blocks={blocks}
          onSave={handleCommit}
          testId="inventory-add-attribute-sections"
          fieldsTestIdPrefix="inventory-add-attribute-fields"
        />
      </AttributeUnsavedStateProvider>

      <div className="flex-1" />

      {/* The footer this surface keeps — see the header note on where it sits.
          The rule and its padding are `InventoryFormCard`'s own portalled
          footer chrome (`shrink-0 border-t px-6 py-4`), bound to rather than
          re-typed, so a pinned add footer and a pinned drawer footer measure
          the same. `-mx-6 px-6` spans it across the sheet's own gutters.

          **Cancel is shown** (VALE-164). The sheet has its own `X`, but on a
          buffered surface an explicit way out belongs beside the write it
          abandons — and it routes through the hosting dialog's unsaved-changes
          guard, so it prompts rather than discarding in silence. `saveLabel` and
          the disabled rule still match what the flag-off view passes
          `InventoryFormCard`.

          **`sticky bottom-0` keeps it on screen** (VALE-285). The `flex-1`
          spacer above pins the footer to the bottom of the *content*, which is
          the bottom of the *panel* only while the record fits — a `mortgage`
          runs past the fold, and the footer went with it: measured at 1280×800,
          Save sat 69px below the viewport on a form whose fields carried on off
          the bottom of the window, which reads as an add form with no Save
          button at all. Sticky pins it to the scrollport instead, so it is in
          view at every scroll position and on every record height.

          `bg-background` is load-bearing, not decoration — without an opaque
          fill the rows scroll visibly through the footer. The spacer stays: it
          is what still puts the footer at the panel's bottom on a *short*
          record, rather than floating mid-panel. */}
      <div className="bg-background sticky bottom-0 -mx-6 mt-6 shrink-0 border-t px-6 pt-4 pb-6">
        <FormActions
          onCancel={onCancel}
          onSave={handleSave}
          hasChanges={dirty}
          disabled={!dirty && !hasOpenDraft}
          isSubmitting={submitting}
          saveLabel="Save"
        />
      </div>
    </div>
  );
}
